import React, {useCallback, useEffect, useState} from 'react'
import {KTIcon} from '../../../_metronic/helpers'
import FilterTrip from './FilterTrip'
import {restRequest} from '../../../api-utils/apiQueries'
import {
  convertToHours,
  formatDateToDDMMYYYY,
} from '../../common-function/formatDateToYearMonth'
import {useNavigate} from 'react-router-dom'
import Pagination from '../../common-function/Pagination'
import {ExportReportPayload, useExportReport} from '../../common-function/downloadExportFile'
import {
  appendDateFilterToBody,
  appendDateFilterToExport,
} from '../../common-function/adminDateFilterPayload'
import {useAdminListState} from '../../common-function/useAdminListState'
import ListFilterToolbar from '../../common-function/ListFilterToolbar'
import ListPageHeader from '../../common-function/ListPageHeader'
import {
  buildTripFilterSummary,
  getDefaultTripFilter,
  TripFilterState,
} from './tripDateRange'

const LIST_STORAGE_KEY = 'trip-management'

const TripManagementWrapper = () => {
  const {runExport, exporting} = useExportReport()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [trips, setTrips] = useState<any[]>([])
  const [showFilter, setShowFilter] = useState<boolean>(false)
  const [totalPages, setTotalPages] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)

  const {
    page,
    setPage,
    searchQuery,
    setSearchQuery,
    activeFilter,
    applyFilter,
    resetToDefault,
    resetPageForSearch,
    isFilterActive,
  } = useAdminListState<TripFilterState>({
    storageKey: LIST_STORAGE_KEY,
    getDefaultFilter: getDefaultTripFilter,
    isDefaultFilter: (f, search) =>
      f.preset === 'today' && f.status.length === 0 && !search.trim(),
  })

  const fetchTripManagementData = useCallback(
    async (query: string) => {
      setIsLoading(true)
      const bodyData: Record<string, unknown> = {
        page,
        search: query,
        status: activeFilter.status,
        type: 'filter',
      }
      appendDateFilterToBody(bodyData, activeFilter)

      try {
        const response: any = await restRequest.getTripsData(bodyData)
        const data = response?.data?.data
        setTrips(data?.data ?? [])
        setTotalPages(data?.pager?.totalPages ?? 0)
        setTotalRecords(data?.pager?.totalRecords ?? 0)
      } catch (error) {
        console.error('Failed to load trip data:', error)
        setTrips([])
        setTotalPages(0)
        setTotalRecords(0)
      } finally {
        setIsLoading(false)
      }
    },
    [page, activeFilter]
  )

  useEffect(() => {
    fetchTripManagementData(searchQuery)
    // Search runs on Enter / magnifier / clear — not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeFilter])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    if (e.target.value === '') {
      resetPageForSearch()
      fetchTripManagementData('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      resetPageForSearch()
      fetchTripManagementData(searchQuery)
    }
  }

  const submitSearch = () => {
    resetPageForSearch()
    fetchTripManagementData(searchQuery)
  }

  const handleExportTrips = () => {
    const payload: ExportReportPayload = {type: 'trip'}
    appendDateFilterToExport(payload, activeFilter)
    if (activeFilter.status.length) payload.status = activeFilter.status
    if (searchQuery.trim()) payload.search = searchQuery.trim()
    runExport(payload)
  }

  const filterSummary = buildTripFilterSummary(activeFilter, searchQuery, totalRecords)

  return (
    <div>
      <p className='card-label fw-bold fs-3 mb-2'>Trip Management</p>

      <ListFilterToolbar
        filterSummary={filterSummary}
        showReset={isFilterActive}
        onReset={resetToDefault}
      />

      <div className='card'>
        <ListPageHeader
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onSearchKeyDown={handleKeyDown}
          onSearchSubmit={submitSearch}
          searchPlaceholder='Search trips by user name'
          isLoading={isLoading}
          isFilterActive={isFilterActive}
          onOpenFilter={() => setShowFilter(true)}
          onExport={handleExportTrips}
          exporting={exporting}
        />
        <div className='card-body py-3'>
          {isLoading ? (
            <div className='d-flex justify-content-center py-10'>
              <div className='spinner-border text-primary'></div>
            </div>
          ) : (
            <div className='table-responsive'>
              {trips.length > 0 ? (
                <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th className='min-w-150px'>Start Address</th>
                      <th className='min-w-140px'>End Address</th>
                      <th className='min-w-150px'>Trip Date</th>
                      <th className='min-w-120px'>Trip Schedule Time</th>
                      <th className='min-w-150px'>Trip Start Time</th>
                      <th className='min-w-150px'>Trip End Time</th>
                      <th className='min-w-120px'>Driver Name</th>
                      <th className='min-w-120px'>Passenger Name/Names</th>
                      <th className='min-w-100px text-end'>Seats Offered</th>
                      <th className='min-w-100px text-end'>Seats confirmed</th>
                      <th className='min-w-100px text-end'>Trip Status</th>
                      <th className='min-w-80px text-end'>View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trips.map((item: any) => {
                      const selectedDate = item.start_date ? new Date(item.start_date) : null
                      const formattedDate =
                        selectedDate && !Number.isNaN(selectedDate.getTime())
                          ? formatDateToDDMMYYYY(selectedDate)
                          : '—'
                      const passengers: string[] = Array.isArray(item.passengers)
                        ? item.passengers
                        : []

                      return (
                        <tr key={item.id}>
                          <td>
                            <p className='text-dark fw-bold text-hover-primary fs-6'>
                              {item.start_address ?? '—'}
                            </p>
                          </td>
                          <td>
                            <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                              {item.end_address ?? '—'}
                            </p>
                          </td>
                          <td>
                            <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                              {formattedDate}
                            </p>
                          </td>
                          <td className='text-center'>
                            <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                              {item.start_desired_time
                                ? convertToHours(+item.start_desired_time)
                                : '—'}
                            </p>
                          </td>
                          <td className='text-center'>
                            <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                              {item.actual_trip_start_time
                                ? convertToHours(+item.actual_trip_start_time)
                                : '—'}
                            </p>
                          </td>
                          <td className='text-center'>
                            <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                              {item.actual_trip_end_time
                                ? convertToHours(+item.actual_trip_end_time)
                                : '—'}
                            </p>
                          </td>
                          <td>
                            <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                              {item.user?.name ?? '—'}
                            </p>
                          </td>
                          <td>
                            {passengers.length > 0 ? (
                              passengers.map((passenger: string, idx: number) => (
                                <p
                                  key={idx}
                                  className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'
                                >
                                  {passenger}
                                </p>
                              ))
                            ) : (
                              <p className='text-muted fs-6 mb-0'>—</p>
                            )}
                          </td>
                          <td className='text-end'>{item.no_of_seats ?? '—'}</td>
                          <td className='text-end'>{item.booked_seat ?? '—'}</td>
                          <td className='text-end'>
                            <span
                              className={`badge ${
                                item.trip_status === 'not_started'
                                  ? 'badge-light-warning'
                                  : item.trip_status === 'cancel'
                                    ? 'badge-light-danger'
                                    : item.trip_status === 'completed'
                                      ? 'badge-light-success'
                                      : 'badge-light-primary'
                              }`}
                            >
                              {item.trip_status ?? '—'}
                            </span>
                          </td>
                          <td className='text-end'>
                            <button
                              type='button'
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                              onClick={() => navigate(`/trip-management/${item.id}`)}
                            >
                              <KTIcon iconName='eye' className='fs-1' />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              ) : (
                <p className='fs-5 pt-3 text-center'>No trips found for this filter.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <Pagination totalPages={totalPages} page={page} setPage={setPage} />
      )}

      <FilterTrip
        showFilter={showFilter}
        toggle={() => setShowFilter(false)}
        activeFilter={activeFilter}
        onApply={applyFilter}
      />
    </div>
  )
}

export default TripManagementWrapper
